import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackPlanPageViewed } from "@/features/analytics/trackers/plan-page-viewed";
import { onboardingQueriesStore } from "@/features/onboarding/api";
import { Disclaimer } from "@/features/onboarding/components/choose-products/disclaimer";
import { NextSteps } from "@/features/onboarding/components/choose-products/next-steps";
import { ProductGroupCard } from "@/features/onboarding/components/choose-products/product-group-card";
import { PurchaseForm } from "@/features/onboarding/components/choose-products/purchase-form";
import { SelectedProduct } from "@/features/onboarding/components/choose-products/selected-product";
import { SendCheckoutLinkCard } from "@/features/onboarding/components/choose-products/send-checkout-link-card";
import { TrustpilotReviews } from "@/features/onboarding/components/choose-products/trustpilot-reviews";
import { WeightLossGoalPlan } from "@/features/onboarding/components/choose-products/weight-loss-goal-plan";
import { WeightLossProgramCard } from "@/features/onboarding/components/choose-products/weight-loss-program-card";
import { WhatsIncluded } from "@/features/onboarding/components/choose-products/whats-included";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import {
  IS_CALL_CENTER_PARAM_KEY,
  onboardingUserSchema,
} from "@/features/onboarding/models";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import type { ProductGroup } from "@/features/products/models";
import { LogoLoader } from "@/shared/components";
import {
  CATEGORY_SLUG,
  PAYMENT_REDIRECT_METHOD_KEY,
  PRODUCT_SLUG,
} from "@/shared/domains";
import { Layout } from "@/shared/ui";

export const PRODUCT_GROUP_CARDS_ID = "product-group-cards";

export const USER_CODE_PARAM_KEY = "code";

const withPricedProducts = (groups: ProductGroup[] = []) =>
  groups
    .map((group) => ({
      ...group,
      products: group.products.filter((product) => product.prices.length > 0),
    }))
    .filter((group) => group.products.length > 0);

export const ChooseProducts = () => {
  const { analytics } = useCustomerIoContext();
  const user = useOnboardingUser();

  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const bmiInputs = useOnboardingStore((state) => state.bmiInputs);
  const higherValue = useOnboardingStore(
    (state) => state.urlParams.higher_value,
  );
  const priceVariant = useOnboardingStore(
    (state) => state.urlParams.price_variant,
  );
  const couponCode = useOnboardingStore((state) => state.couponCode);
  const urlParamsResolved = useOnboardingStore(
    (state) => state.urlParamsResolved,
  );

  const patchPersonalDetails = useOnboardingStore(
    (state) => state.patchPersonalDetails,
  );
  const patchContactDetails = useOnboardingStore(
    (state) => state.patchContactDetails,
  );
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const setIsCallCenter = useOnboardingStore((state) => state.setIsCallCenter);
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const [searchParams] = useSearchParams();

  const userCode = onboardingUserSchema.shape.code.safeParse(
    searchParams.get(USER_CODE_PARAM_KEY),
  ).data;

  const isPaymentRedirect = searchParams.has(PAYMENT_REDIRECT_METHOD_KEY);

  const isCallCenterParam = z
    .stringbool()
    .safeParse(searchParams.get(IS_CALL_CENTER_PARAM_KEY)).data;

  useEffect(() => {
    if (isPaymentRedirect) {
      return;
    }

    setIsCallCenter(Boolean(isCallCenterParam));
  }, [isCallCenterParam, isPaymentRedirect, setIsCallCenter]);

  const { data: productGroups, isLoading: isLoadingProductGroups } = useQuery({
    ...productsQueriesStore.products.category._ctx.products({
      categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
      higherValue,
      priceVariant,
      couponCode,
    }),
    // Wait for urlParamsResolved so this never fires with priceVariant still
    // undefined-because-not-yet-read (as opposed to genuinely absent) - see
    // DEV-73391. Without this, a reload landing directly on this screen can
    // fetch and auto-select the default-priced product before the correctly
    // scoped response arrives, and that stale selection is never corrected.
    enabled: urlParamsResolved,
  });

  const {
    data: onboardingUser,
    isSuccess: isSuccessOnboardingUser,
    isLoading: isLoadingOnboardingUser,
  } = useQuery({
    ...onboardingQueriesStore.onboarding.user(userCode),
    enabled: Boolean(userCode),
  });

  useEffect(() => {
    if (!isSuccessOnboardingUser) {
      return;
    }

    setSelectedCategories(onboardingUser.selectedCategories);
    patchPersonalDetails({ firstName: onboardingUser.firstName });
    patchContactDetails({
      code: onboardingUser.code,
      email: onboardingUser.email,
      phoneNumber: onboardingUser.phoneNumber,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessOnboardingUser]);

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    trackPlanPageViewed({
      customerIoAnalytics: analytics,
      user: user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addSelectedProduct = useOnboardingStore(
    (state) => state.addSelectedProduct,
  );

  useEffect(() => {
    if (!productGroups) {
      return;
    }

    const tirzepatideProduct = withPricedProducts(productGroups)
      .flatMap((group) => group.products)
      .find((product) => product.slug === PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE);

    const alreadySelected = selectedCategories.some(
      (category) => category.selectedProduct?.id === tirzepatideProduct?.id,
    );

    if (tirzepatideProduct && !alreadySelected) {
      addSelectedProduct(tirzepatideProduct);
    }
  }, [productGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoadingOnboardingUser && !onboardingUser && !bmiInputs) {
    return <Navigate to={ONBOARDING_ROUTE.BMI} replace />;
  }

  return (
    <Layout.Content size="sm">
      <LogoLoader
        show={
          !urlParamsResolved ||
          isLoadingProductGroups ||
          isLoadingOnboardingUser
        }
        fullscreen
      />

      <div className="flex w-full flex-col gap-12 sm:gap-16">
        <WeightLossGoalPlan />

        <div className="flex flex-col gap-4">
          <WhatsIncluded />

          <NextSteps />
        </div>

        <section id={PRODUCT_GROUP_CARDS_ID} className="flex flex-col gap-4">
          {withPricedProducts(productGroups).map((group) => (
            <ProductGroupCard key={group.id} group={group} />
          ))}
        </section>

        <div className="flex flex-col gap-8">
          <SelectedProduct />

          <PurchaseForm />

          {isCallCenter && onboardingUser && (
            <SendCheckoutLinkCard
              code={onboardingUser.code}
              email={onboardingUser.email}
            />
          )}
        </div>

        <TrustpilotReviews />

        <WeightLossProgramCard />

        <Disclaimer />
      </div>
    </Layout.Content>
  );
};
